import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  TextField,
  Button,
  useTheme,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import { Auth } from 'aws-amplify';
import SelectedDealDisplay from '../deals/selectedDeal/SelectedDealDisplay';
import DealRepository from '../../repository/DealRepository';
import { GamifyToast } from '../../common/CustomToasts';
import './DealDetailPage.styles.css';

const DealDetailPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { dealId } = useParams<{ dealId: string }>();
  const [dealData, setDealData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchDealData() {
      if (!dealId) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await DealRepository.fetchDeal(dealId, null);
        if (data) {
          setDealData(data);
        }
      } catch (err) {
        console.error('Error fetching deal:', err);
        setError(
          err instanceof Error ? err : new Error('Failed to fetch deal data')
        );
        GamifyToast.error('Failed to load deal data');
      } finally {
        setIsLoading(false);
      }
    }

    fetchDealData();
  }, [dealId]);

  const handleRefetchDeal = async () => {
    if (!dealId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await DealRepository.fetchDeal(dealId, null);
      setDealData(data);
    } catch (err) {
      console.error('Error refetching deal:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to refetch deal data')
      );
    } finally {
      setIsLoading(false);
    }
  };

  // DealRepository.fetchDeal returns a listEntry with: deal, mainDisplay, comments, appointments, etc.
  const deal = dealData?.deal;
  const mainDisplay = dealData?.mainDisplay || {};

  const selectedDealProps = {
    deal,
    isOnKanban: false,
    ...mainDisplay, // spreads mainDisplayAttrs, fullName, dealStage
    appointments: dealData?.appointments || [],
    comments: dealData?.comments || [],
    metaData: dealData?.dealMetaData,
    dealId,
    categoryId: deal?.categoryID,
    stageId: dealData?.currentStageID,
    userId: deal?.currentOwnerID,
    setter: deal?.setter,
    closer: deal?.closer,
    onTriggerSelectedDealRefetch: handleRefetchDeal,
    handleRefetchDeals: handleRefetchDeal,
    setAddDealOpen: undefined,
    setSortOpen: undefined,
    setSelectedDealMetaDataOpen: undefined,
    selectedDealMetaDataOpen: false,
    handleOpenOwnerDealStageModal: undefined,
    handleOpenChargeModal: undefined,
    handleDeleteDeal: undefined,
    hideDealMetaData: true,
    hideDealOptionsMenu: true,
    isReadOnly: true, // Estimators/external users get read-only access
  };

  const renderLoading = () => (
    <Box
      className="deal-detail-loading"
      sx={{ backgroundColor: theme.custom.background3 }}
    >
      <CircularProgress sx={{ color: theme.custom.primary }} />
    </Box>
  );

  const renderError = () => (
    <Box
      className="deal-detail-error"
      sx={{ backgroundColor: theme.custom.background3 }}
    >
      <Typography variant="h6" sx={{ mb: 2, color: theme.custom.primary }}>
        Error Loading Deal
      </Typography>
      <Typography variant="body2" sx={{ color: theme.custom.textLight }}>
        {error?.message || 'An unknown error occurred'}
      </Typography>
    </Box>
  );

  const renderDealContent = () => (
    <Box className="deal-detail-scroll">
      <div className="deal-detail-content">
        <SelectedDealDisplay props={selectedDealProps} />
      </div>
    </Box>
  );

  const renderNotFound = () => (
    <Box
      className="deal-detail-not-found"
      sx={{ backgroundColor: theme.custom.background3 }}
    >
      <Typography variant="h6" sx={{ color: theme.custom.text }}>
        Deal not found
      </Typography>
      <Typography variant="body2" sx={{ color: theme.custom.textLight, mt: 1 }}>
        The deal you are looking for does not exist or you do not have access to
        it.
      </Typography>
    </Box>
  );

  const [dealLink, setDealLink] = useState('');
  const [linkError, setLinkError] = useState('');

  const extractDealIdFromLink = (link: string): string | null => {
    try {
      // Handle full URLs like https://webapp.gogamify.com/deal/abc123
      if (link.includes('/deal/')) {
        const match = link.match(/\/deal\/([^/?#]+)/);
        return match ? match[1] : null;
      }
      // Handle just the ID being pasted
      if (link.trim() && !link.includes('/')) {
        return link.trim();
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleGoToDeal = () => {
    const extractedId = extractDealIdFromLink(dealLink);
    if (extractedId) {
      setLinkError('');
      navigate(`/deal/${extractedId}`);
    } else {
      setLinkError('Please enter a valid deal link or ID');
    }
  };

  const handleLinkKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleGoToDeal();
    }
  };

  const renderNoDealId = () => (
    <Box
      className="deal-detail-not-found"
      sx={{
        backgroundColor: theme.custom.background3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: 4,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="h5"
        sx={{ color: theme.custom.text, fontWeight: 600, mb: 2 }}
      >
        Welcome to PAIV
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: theme.custom.textLight, maxWidth: 450, mb: 4 }}
      >
        Paste the deal link you received below to view the deal details.
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 450 }}>
        <TextField
          fullWidth
          placeholder="Paste deal link here (e.g., https://webapp.gogamify.com/deal/abc123)"
          value={dealLink}
          onChange={(e) => {
            setDealLink(e.target.value);
            setLinkError('');
          }}
          onKeyPress={handleLinkKeyPress}
          error={!!linkError}
          helperText={linkError}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.custom.background2,
              '& fieldset': {
                borderColor: theme.custom.stroke,
              },
              '&:hover fieldset': {
                borderColor: theme.custom.primary,
              },
            },
            '& .MuiInputBase-input': {
              color: theme.custom.text,
            },
          }}
        />
        <Button
          variant="contained"
          fullWidth
          onClick={handleGoToDeal}
          disabled={!dealLink.trim()}
          sx={{
            backgroundColor: theme.custom.primary,
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            '&:hover': {
              backgroundColor: theme.custom.primary,
              opacity: 0.9,
            },
            '&:disabled': {
              backgroundColor: theme.custom.stroke,
              color: theme.custom.textLight,
            },
          }}
        >
          View Deal
        </Button>
      </Box>

      <Typography
        variant="body2"
        sx={{ color: theme.custom.textLight, mt: 4, opacity: 0.7 }}
      >
        If you don&apos;t have a link, please contact your administrator.
      </Typography>

      <Button
        variant="text"
        size="small"
        startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
        onClick={() => Auth.signOut()}
        sx={{
          mt: 6,
          color: theme.custom.textLight,
          textTransform: 'none',
          fontSize: 13,
          opacity: 0.7,
          '&:hover': {
            opacity: 1,
            backgroundColor: 'transparent',
          },
        }}
      >
        Sign out
      </Button>
    </Box>
  );

  return (
    <Box
      className="deal-detail-page"
      sx={{ backgroundColor: theme.custom.background1 }}
    >
      {/* {renderHeader()} */}
      <Box
        className="deal-detail-body"
        sx={{ backgroundColor: theme.custom.background3 }}
      >
        {!dealId && renderNoDealId()}
        {dealId && isLoading && renderLoading()}
        {dealId && error && !isLoading && renderError()}
        {dealId && !isLoading && !error && dealData && renderDealContent()}
        {dealId && !isLoading && !error && !dealData && renderNotFound()}
      </Box>
    </Box>
  );
};

export default DealDetailPage;
